import { useCallback, useEffect, useRef, useState } from 'react';

import { isPartnerAccessEnabled } from '../utils/partnerAccess/partnerFlag';
import { loadGroupSummary, loadPartnerDirectory } from '../utils/partnerAccess/partnerDirectory';

// Nothing has answered yet: no directory, and no failure to tell either.
// What the hook holds carries the user it was asked for (`userId`).
const NO_ANSWER = {
  userId: null,
  directory: null,
  unavailable: false,
  answered: false,
};
const NO_SUMMARY = { userId: null, counters: null };

/**
 * The sedes of a partner, for whoever lists them: the profile menu while it is
 * open, the chat of an account without channels. Nothing is asked for while
 * `open` is false, and nothing at all with the build flag off. `directory`
 * stays `null` for anyone who is not a partner; `summary` (who is waiting in
 * each sede) is a courtesy and stays `null` when it is not available.
 * `unavailable` tells a directory that was asked for and could not be loaded
 * (rate limit, outage) from one that is not there yet or does not exist.
 * `loading` is true from the first render of a load that is asked for until
 * its answer, so a caller can wait instead of taking "no directory yet" for
 * "not a partner"; a later reload keeps the last answer on screen meanwhile.
 * `retry` asks again, which is how a caller gets out of `unavailable`.
 *
 * An answer belongs to the user it was asked for. When `userId` changes while
 * the hook stays mounted, what it holds for the previous user is hidden at
 * once (so `loading` is true again while the new user's load is due) and an
 * answer that arrives late for the previous user is dropped.
 */
const usePartnerDirectory = ({ open, userId }) => {
  // One state for the answer: directory, failure and "it answered" change
  // together, never one render apart.
  const [storedAnswer, setAnswer] = useState(NO_ANSWER);
  const [storedSummary, setSummary] = useState(NO_SUMMARY);
  const [attempt, setAttempt] = useState(0);

  // The user of the latest render. A load reads it when its answer arrives:
  // React runs the cleanup that cancels the load of the previous user some
  // time after rendering for the new one, and an answer can land in between.
  const latestUserId = useRef(userId);
  latestUserId.current = userId;

  const requested = Boolean(open && userId) && isPartnerAccessEnabled();

  useEffect(() => {
    if (!requested) return undefined;

    let cancelled = false;
    const isStale = () => cancelled || latestUserId.current !== userId;
    const load = async () => {
      // Neither call rejects: a failure is a status, or no counters.
      const found = await loadPartnerDirectory({ userId });
      if (isStale()) return;
      setAnswer({
        userId,
        directory: found.status === 'partner' ? found : null,
        unavailable: found.status === 'unavailable',
        answered: true,
      });
      if (found.status !== 'partner') return;
      const counters = await loadGroupSummary();
      if (!isStale()) setSummary({ userId, counters });
    };
    load();

    return () => {
      cancelled = true;
    };
  }, [requested, userId, attempt]);

  const retry = useCallback(() => {
    setAnswer(NO_ANSWER);
    setAttempt((count) => count + 1);
  }, []);

  // Only what was asked for the current user is shown; with no user, nothing.
  const answer = userId && storedAnswer.userId === userId ? storedAnswer : NO_ANSWER;
  const summary = userId && storedSummary.userId === userId ? storedSummary.counters : null;

  return {
    directory: answer.directory,
    summary,
    unavailable: answer.unavailable,
    loading: requested && !answer.answered,
    retry,
  };
};

export default usePartnerDirectory;
